import { api } from './axios';

export type AttendanceGroup = 'STUDENT' | 'STAFF' | 'BOTH';
export type AttendanceStatus = 'PRESENT' | 'ABSENT' | 'LATE' | 'HALF_DAY' | 'LEAVE';
export type HalfDayType = 'MORNING' | 'AFTERNOON';
export type HolidayType = 'PUBLIC_HOLIDAY' | 'SCHOOL_HOLIDAY' | 'ADHOC_OFF';
export type DayMode = 'WORKING' | 'WEEKLY_OFF' | 'HOLIDAY';

export interface Schedule {
  id: number;
  attendanceGroup: AttendanceGroup;
  weeklyOffDays: number[]; // 0=Mon … 6=Sun
  effectiveFrom: string;
  effectiveTo: string | null;
  current: boolean;
}

export interface ScheduleInput {
  attendanceGroup: AttendanceGroup;
  weeklyOffDays: number[];
  effectiveFrom: string;
}

export interface Holiday {
  id: number;
  holidayDate: string;
  name: string;
  appliesTo: AttendanceGroup;
  holidayType: HolidayType;
}

export interface HolidayInput {
  holidayDate: string;
  name: string;
  appliesTo: AttendanceGroup;
  holidayType: HolidayType;
}

export interface AttendanceMark {
  status: AttendanceStatus;
  halfDayType: HalfDayType | null;
  reason: string | null;
}

export interface SheetRow {
  enrollmentId: number;
  studentId: number | null;
  registrationNumber: string | null;
  firstName: string | null;
  lastName: string | null;
  mark: AttendanceMark | null;
}

export interface DailySheet {
  classSectionId: number;
  className: string;
  sectionName: string;
  date: string;
  mode: DayMode;
  offReason: string | null;
  rows: SheetRow[];
}

export interface SheetSubmissionRow {
  enrollmentId: number;
  status: AttendanceStatus;
  halfDayType?: HalfDayType | null;
  reason?: string | null;
}

export interface SheetSubmission {
  classSectionId: number;
  date: string;
  rows: SheetSubmissionRow[];
}

export interface DayMark {
  date: string;
  status: AttendanceStatus;
  halfDayType: HalfDayType | null;
}

export interface StudentMonth {
  enrollmentId: number;
  studentId: number | null;
  registrationNumber: string | null;
  firstName: string | null;
  lastName: string | null;
  present: number;
  absent: number;
  late: number;
  halfDay: number;
  leave: number;
  attendancePercent: number;
  daily: DayMark[];
}

export interface MonthlySummary {
  classSectionId: number;
  className: string;
  sectionName: string;
  year: number;
  month: number;
  workingDates: string[];
  offDates: string[];
  students: StudentMonth[];
}

export const attendanceApi = {
  listSchedules: (group: AttendanceGroup) =>
    api.get<Schedule[]>('/attendance/schedules', { params: { group } }).then((r) => r.data),
  createSchedule: (body: ScheduleInput) =>
    api.post<Schedule>('/attendance/schedules', body).then((r) => r.data),

  listHolidays: (params: { from: string; to: string; group?: AttendanceGroup }) =>
    api.get<Holiday[]>('/attendance/holidays', { params }).then((r) => r.data),
  createHoliday: (body: HolidayInput) =>
    api.post<Holiday>('/attendance/holidays', body).then((r) => r.data),
  updateHoliday: (id: number, body: HolidayInput) =>
    api.put<Holiday>(`/attendance/holidays/${id}`, body).then((r) => r.data),
  deleteHoliday: (id: number) => api.delete<void>(`/attendance/holidays/${id}`).then((r) => r.data),

  dailySheet: (classSectionId: number, date: string) =>
    api.get<DailySheet>('/attendance/sheet', { params: { classSectionId, date } }).then((r) => r.data),
  submitSheet: (body: SheetSubmission) =>
    api.post<{ saved: number }>('/attendance/sheet', body).then((r) => r.data),

  monthlySummary: (classSectionId: number, year: number, month: number) =>
    api
      .get<MonthlySummary>('/attendance/summary', { params: { classSectionId, year, month } })
      .then((r) => r.data),
};
